import React from 'react';
import pathToRegexp from 'path-to-regexp';
import BigNumber from 'bignumber.js';
import { Notice } from 'components/common/Notice';
import Loader from 'components/common/Loader';
import moment from 'common/moment';
import _ from 'common/underscore';
import Wahanda from 'common/wahanda';
import { DailySalesReportAnalytics } from 'common/analytics';
import DISPLAY from '../displayStateEnum';
import Filters from '../common/Filters';
import { CHANNEL } from '../BookingChannelFilter';
import { allEmployeesOptionValue } from '../EmployeeFilter';
import { ReportTable } from './reportTable';
import DailySalesReportGraph from './DailySalesReportGraph';
import ReportLayout from '../common/ReportLayout';
import ROUTES from '../SalesReportContainer/reportRoutes';

interface IDailySalesReportProps extends React.HTMLAttributes<Element> {
  requestDailySalesReportDataAction: (...args: any[]) => any;
  history: {
    push: (...args: any[]) => any;
  };
  match: {
    params: {
      venueId: string;
      fromDate?: string;
      toDate?: string;
      selectedChannel?: string;
      employeeFilter?: string;
    };
  };
  reportData?: any;
  displayState: any;
  map?: any;
  rows?: any;
  params?: any;
  push?: any;
}

export class DailySalesReport extends React.Component<IDailySalesReportProps, {}> {
  static defaultProps = {
    reportData: null,
  };

  componentDidMount = () => {
    DailySalesReportAnalytics.trackPageView();
    this.requestDataFromApi();
  };

  componentDidUpdate = (prevProps) => {
    if (!_.isEqual(this.props.match.params, prevProps.match.params)) {
      this.requestDataFromApi();
    }
  };

  onFilterChange = (filters) => {
    const { venueId } = this.getQueryParams();
    const { fromDate, toDate, selectedChannel, selectedEmployeeId } = filters;
    const toPath = pathToRegexp.compile(ROUTES.DAILY_SALES);
    const newPath = toPath({
      venueId,
      fromDate: fromDate.formatApiDateString(),
      toDate: toDate.formatApiDateString(),
      employeeFilter:
        selectedChannel != null && selectedEmployeeId == null
          ? allEmployeesOptionValue
          : selectedEmployeeId,
      selectedChannel,
    });
    this.props.history.push(newPath);
  };

  getQueryParams = () => {
    const { venueId, fromDate, toDate, employeeFilter, selectedChannel } = _.defaults(
      this.props.match.params,
      {
        fromDate: moment().startOf('month').formatApiDateString(),
        toDate: moment().endOf('month').formatApiDateString(),
        employeeFilter: allEmployeesOptionValue,
        selectedChannel: CHANNEL.ALL,
      },
    );
    return {
      venueId,
      fromDate: moment(fromDate),
      toDate: moment(toDate),
      selectedChannel,
      employeeFilter: new BigNumber(employeeFilter).toNumber(),
    };
  };

  getLoadingSpinner = () => <Loader />;

  getErrorMessage = () => <div>{Wahanda.lang.reports.sales.error}</div>;

  trackDateFilterChange = (data) => {
    DailySalesReportAnalytics.trackReportDateFilterChange(data.type);
  };

  requestDataFromApi = () => {
    const { venueId, fromDate, toDate, selectedChannel, employeeFilter } = this.getQueryParams();
    this.props.requestDailySalesReportDataAction(
      venueId,
      fromDate,
      toDate,
      selectedChannel === CHANNEL.ALL ? null : selectedChannel,
      employeeFilter === allEmployeesOptionValue ? null : employeeFilter,
    );
  };

  render() {
    let body;
    switch (this.props.displayState) {
      case DISPLAY.WAITING:
        body = this.getLoadingSpinner();
        break;
      case DISPLAY.REPORT: {
        const { venueId, selectedChannel, employeeFilter } = this.getQueryParams();
        body = (
          <ReportTable
            venueId={venueId}
            reportData={this.props.reportData}
            selectedChannel={selectedChannel}
            selectedEmployeeId={employeeFilter}
          />
        );
        break;
      }
      case DISPLAY.ERROR:
        body = this.getErrorMessage();
        break;
      default:
        throw new Error(`Unrecognised display state: ${this.props.displayState}`);
    }
    const dateFilterProps = {
      uniqueId: 'dailySalesReportDateFilter',
      fromDate: this.getQueryParams().fromDate,
      toDate: this.getQueryParams().toDate,
    };
    const filters = (
      <Filters
        onDatePickerChange={this.trackDateFilterChange}
        date={dateFilterProps}
        channel={{ selectedChannel: this.getQueryParams().selectedChannel }}
        employee={{
          categoryFilterEnabled: true,
          selectedEmployeeId: this.getQueryParams().employeeFilter,
        }}
        onFilterChange={this.onFilterChange}
        onChannelChange={DailySalesReportAnalytics.trackChannelFilterChange}
        onEmployeeChange={DailySalesReportAnalytics.trackEmployeeFilterChange}
      />
    );
    const graph =
      this.props.displayState === DISPLAY.REPORT ? (
        <DailySalesReportGraph
          data={this.props.reportData.rows.map((row) => ({
            date: row.date,
            serviceGrossTotal: row.serviceGrossTotal,
            productGrossTotal: row.productGrossTotal,
            voucherGrossTotal: row.voucherGrossTotal,
          }))}
        />
      ) : null;
    const messages = (
      <Notice type="success">
        <p
          dangerouslySetInnerHTML={{
            __html: Wahanda.lang.reports.newReportsMessage,
          }}
        />
      </Notice>
    );
    return (
      <ReportLayout
        messages={messages}
        filter={filters}
        showTimeStamp={this.props.displayState === DISPLAY.REPORT}
        body={body}
        graph={graph}
      />
    );
  }
}
